import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConsentData } from '|/app/purchase/checkout/steps/review-order/cio/hooks/use-consent-data';
import {
  Dashboard,
  Step,
  StepName,
  Summary,
  getDashboard,
  getSummary,
  rollBack
} from '|/app/services';
import { useBrand } from '|/brand';
import { setCurrentStepName } from '|/helpers/current-step-tracker';
import useGapWaiverData from '|/hooks/use-gap-waiver-data';
import useRumAction from '|/hooks/use-rum-action';
import {
  getReviewSummary,
  placeOrder,
  saveConsent,
  ConsentPayload,
  ReviewSummaryResponse
} from '|/services/purchase-api';
import { VERIFICATION_STEP_IDS } from './constants';
import usePartialCompletion from './partial-completion/use-partial-completion';
import { setChatPageContext } from '../constants/chat-page-context';
import { getChatSurface } from '../constants/chat-surface-mappings';

export type VerificationsPhase =
  | 'checking-consents'
  | 'place-order-initial'
  | 'placing-order'
  | 'place-order-error'
  | 'checking'
  | 'tasks'
  | 'no-tasks'
  | 'tasks-complete'
  | 'only-kiq-complete';

interface UseVerificationsVmProps {
  initialVerificationSteps: Step[];
  activeStepUrl: string;
  setActiveStepUrl: (url: string) => void;
  onStepComplete: () => void;
  onVerificationGroupComplete: () => void;
  summary: ReviewSummaryResponse | null;
  isSlotRetainedAfterRollback: boolean;
  setDashboard: (d: Dashboard) => void;
  setSummary: (s: Summary) => void;
}

function isVerificationGroupComplete(steps: Step[]): boolean {
  if (steps.length === 0) return false;
  return steps.every(s => s.isComplete);
}

/** Derives phase from the current verification steps snapshot. afterTasksPhase = true when
 *  triggered by a non-IV task submit (so "nothing remaining" maps to tasks-complete). */
function derivePhase(steps: Step[], afterTasksPhase: boolean): VerificationsPhase {
  const placeOrderStep = steps.find(s => s.id === StepName.PlaceOrder);
  if (!placeOrderStep?.isComplete) return 'place-order-initial';

  const hasIncompleteTask = steps.some(s => s.id !== StepName.PlaceOrder && !s.isComplete);
  if (hasIncompleteTask) return 'tasks';

  const stepsInFinalDetails = steps.filter(s => s.id !== StepName.PlaceOrder);

  if (stepsInFinalDetails.length > 0 && stepsInFinalDetails.every(s => s.isComplete)) {
    const onlyKiqStep = stepsInFinalDetails.every(s => s.id === StepName.IdentityVerification);
    if (onlyKiqStep) return 'only-kiq-complete';
    return 'tasks-complete';
  }

  return afterTasksPhase ? 'tasks-complete' : 'no-tasks';
}

function getVerifStepsFromDashboard(dashboard: { steps: Step[] }): Step[] {
  return dashboard.steps.filter(
    s => VERIFICATION_STEP_IDS.has(s.id) && s.section === 2 && s.isEnabled
  );
}

// Module-scoped: the accordion unmounts this fold on collapse, so a remount
// mid-request (or a StrictMode dev double-invoke) must join the pending
// place-order rather than fire a duplicate. Component state can't survive that.
let placeOrderInFlight: Promise<void> | null = null;

async function submitPlaceOrderOnce(buildPayload: () => ConsentPayload[]): Promise<void> {
  if (!placeOrderInFlight) {
    placeOrderInFlight = (async () => {
      await saveConsent({ consents: buildPayload() }, new AbortController().signal);
      await placeOrder(new AbortController().signal);
    })();
  }
  try {
    await placeOrderInFlight;
  } finally {
    placeOrderInFlight = null;
  }
}

const useVerificationsVm = ({
  initialVerificationSteps,
  activeStepUrl,
  setActiveStepUrl,
  onStepComplete,
  onVerificationGroupComplete,
  summary,
  isSlotRetainedAfterRollback,
  setDashboard,
  setSummary
}: UseVerificationsVmProps) => {
  const navigate = useNavigate();
  const { brand } = useBrand();
  const { trackAction } = useRumAction();
  const [verificationSteps, setVerificationSteps] = useState<Step[]>(initialVerificationSteps);
  const [phase, setPhase] = useState<VerificationsPhase>('checking-consents');
  const hasMadeConsentDecisionRef = useRef(false);
  const [openTaskStepId, setOpenTaskStepId] = useState<StepName | null>(null);

  const {
    consentHtml,
    agreementStates,
    updateAgreement,
    buildConsentPayload,
    areAgreementsComplete,
    shouldDisplayNRSF,
    shouldDisplayMarylandNRSF,
    isCarvanaFinanced,
    previouslyAdoptedConsents,
    isReady: isConsentDataReady
  } = useConsentData(summary);

  const { shouldShowGapWaiverModal } = useGapWaiverData();
  const [isGapWaiverRemovedModalOpen, setIsGapWaiverRemovedModalOpen] = useState(false);

  const handleGapWaiverModalClose = useCallback(async () => {
    setIsGapWaiverRemovedModalOpen(false);
    try {
      await rollBack({ cancelTransfer: true, keepReviewOrder: false });
    } catch {
      // best effort
    }
    navigate('/next-step');
  }, [navigate]);

  const hasConsents = !areAgreementsComplete();

  // The "current" step is the first incomplete non-PlaceOrder step — used for partial completion
  const currentStepId =
    verificationSteps.find(s => s.id !== StepName.PlaceOrder && !s.isComplete)?.id ?? null;
  const partialCompletionText = usePartialCompletion(currentStepId);

  // Deep-link auto-open on mount
  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) return;
    didMountRef.current = true;

    if (activeStepUrl && activeStepUrl !== '_verification') {
      const targetStep = verificationSteps.find(s => s.url === activeStepUrl);
      if (targetStep) {
        setOpenTaskStepId(targetStep.id);
        // Do NOT call setActiveStepUrl here — it was already set by use-checkout-vm hash handler
      }
    }
  }, [activeStepUrl, verificationSteps]);

  useEffect(() => {
    if (openTaskStepId === null) return;
    const openStep = verificationSteps.find(s => s.id === openTaskStepId);
    if (!openStep?.url) return;
    const subStep = `verification.${openStep.url}`;
    setCurrentStepName(subStep);
    datadogRum.setGlobalContextProperty('checkoutStep', subStep);
    return () => {
      // Restore the parent fold value so analytics reflect "verification" when the modal closes
      setCurrentStepName('verification');
      datadogRum.setGlobalContextProperty('checkoutStep', 'verification');
    };
  }, [openTaskStepId, verificationSteps]);

  useEffect(() => {
    if (openTaskStepId === null) return;
    const openStep = verificationSteps.find(s => s.id === openTaskStepId);
    if (!openStep?.url) return;
    const chatSurface = getChatSurface(openStep.url, brand);
    if (!chatSurface) return;
    setChatPageContext(chatSurface);
    return () => {
      const verificationSurface = getChatSurface('_verification', brand);
      if (verificationSurface) {
        setChatPageContext(verificationSurface);
      }
    };
  }, [openTaskStepId, verificationSteps, brand]);

  const refetchAndDerive = useCallback(
    async (afterTasksPhase: boolean, autoAdvanceIfComplete = false): Promise<Dashboard | null> => {
      const [freshDashboard, freshSummary] = await Promise.all([getDashboard(), getSummary()]);
      if (!freshDashboard) return null;
      setDashboard(freshDashboard);
      if (freshSummary) setSummary(freshSummary);
      const freshSteps = getVerifStepsFromDashboard(freshDashboard);
      setVerificationSteps(freshSteps);
      const derivedPhase = derivePhase(freshSteps, afterTasksPhase);
      if (
        autoAdvanceIfComplete &&
        (derivedPhase === 'no-tasks' ||
          derivedPhase === 'tasks-complete' ||
          derivedPhase === 'only-kiq-complete')
      ) {
        onVerificationGroupComplete();
        onStepComplete();
      } else {
        setPhase(derivedPhase);
      }
      return freshDashboard;
    },
    [setDashboard, setSummary, onStepComplete, onVerificationGroupComplete]
  );

  const handlePlaceOrderSubmit = useCallback(async () => {
    setPhase('placing-order');
    try {
      await submitPlaceOrderOnce(buildConsentPayload);
      await refetchAndDerive(false, true);
    } catch {
      try {
        const orderSummary = await getReviewSummary(new AbortController().signal);
        const shouldShowModal = await shouldShowGapWaiverModal(orderSummary);
        if (shouldShowModal) {
          setIsGapWaiverRemovedModalOpen(true);
          return;
        }
      } catch {
        // If gap waiver check fails, fall through to default error
      }
      setPhase('place-order-error');
    }
  }, [refetchAndDerive, buildConsentPayload, shouldShowGapWaiverModal]);

  const handlePlaceOrderTap = useCallback(() => {
    void handlePlaceOrderSubmit();
  }, [handlePlaceOrderSubmit]);

  // Sole exit edge from 'checking-consents'. Gated on isConsentDataReady so we
  // never decide based on stale defaults (e.g. NRSF bucket still loading).
  useEffect(() => {
    if (phase !== 'checking-consents') return;
    if (!isConsentDataReady) return;
    // The phase check alone doesn't stop a second run: StrictMode re-runs this effect before the
    // phase change commits, which ran the auto-fire twice and advanced the accordion twice.
    if (hasMadeConsentDecisionRef.current) return;
    hasMadeConsentDecisionRef.current = true;
    const placeOrderStep = verificationSteps.find(s => s.id === StepName.PlaceOrder);
    if (placeOrderStep?.isComplete) {
      setPhase(derivePhase(verificationSteps, false));
      return;
    }
    // Even with every consent adopted, a slot that survived an advocate rollback waits for an explicit tap.
    const shouldAutoFire = !hasConsents && !isSlotRetainedAfterRollback;
    // Infrastructure-level decision event (bypasses analytics) — lets us measure
    // how often re-entries fall back to the button on stale consent reads.
    trackAction('final_details_consent_decision', {
      autoFired: shouldAutoFire,
      heldForRetainedSlot: !hasConsents && isSlotRetainedAfterRollback,
      adoptedCount: previouslyAdoptedConsents.size
    });
    if (!shouldAutoFire) {
      setPhase('place-order-initial');
    } else {
      void handlePlaceOrderSubmit();
    }
  }, [
    phase,
    isConsentDataReady,
    hasConsents,
    isSlotRetainedAfterRollback,
    verificationSteps,
    handlePlaceOrderSubmit,
    trackAction,
    previouslyAdoptedConsents
  ]);

  const handleTaskSubmit = useCallback(
    async (stepId: StepName) => {
      setOpenTaskStepId(null);
      if (stepId === StepName.IdentityVerification) {
        setVerificationSteps(prev =>
          prev.map(s => (s.id === stepId ? { ...s, isComplete: true } : s))
        );
        setPhase('checking');
      }
      const freshDashboard = await refetchAndDerive(stepId !== StepName.IdentityVerification);
      const placeOrderRolledBack = !freshDashboard?.steps.some(
        s => s.id === StepName.PlaceOrder && s.isComplete
      );
      if (placeOrderRolledBack) {
        navigate('/next-step');
        return;
      }
      if (
        freshDashboard &&
        isVerificationGroupComplete(getVerifStepsFromDashboard(freshDashboard))
      ) {
        onVerificationGroupComplete();
      }
    },
    [refetchAndDerive, navigate, onVerificationGroupComplete]
  );

  const handleSheetOpen = useCallback((step: Step) => {
    setOpenTaskStepId(step.id);
  }, []);

  const handleSheetClose = useCallback(() => {
    setOpenTaskStepId(null);
    setActiveStepUrl('_verification');
    // partialCompletionText re-derives via usePartialCompletion(currentStepId) on next render
  }, [setActiveStepUrl]);

  const handlePlaceOrderErrorDismiss = useCallback(() => {
    setPhase('place-order-initial');
  }, []);

  const handleContinue = useCallback(() => {
    onStepComplete();
  }, [onStepComplete]);

  return {
    phase,
    verificationSteps,
    openTaskStepId,
    partialCompletionText,
    handlePlaceOrderTap,
    handlePlaceOrderErrorDismiss,
    hasConsents,
    consentProps: {
      consentHtml,
      agreementStates,
      updateAgreement,
      shouldDisplayNRSF,
      shouldDisplayMarylandNRSF,
      isCarvanaFinanced,
      areAgreementsComplete,
      previouslyAdoptedConsents
    },
    handleTaskSubmit,
    handleSheetOpen,
    handleSheetClose,
    handleContinue,
    isGapWaiverRemovedModalOpen,
    handleGapWaiverModalClose
  };
};

export default useVerificationsVm;
